<template>
  <WrappedForm
    v-show="show"
    ref="form"
    class="job-description-info"
    label-width="216px"
    :model="stepData"
    :formLines="formLines"
    :disabled="disabled"
  >
    <template #customContentEditor="{ lineName }">
      <CustomContentEditor :disabled="disabled" :ref="`customContent_${lineName}`" :content-list="stepData[lineName]"></CustomContentEditor>
    </template>
    <template #jobDetailEditor="{ lineName }">
      <TinymceEditor
        v-if="showTinymce && !hiddenTinymce && show"
        v-model="stepData[lineName]"
        :hiddenImage="hiddenImage"
        :disabled="isNotEditableJobTemplate || disabled"
      ></TinymceEditor>
    </template>
    <template #videoNavigation="{ lineName, line }">
      <VideoNavigationEditor v-bind="line.props" :disabled="disabled" :ref="`navigation_${lineName}`"></VideoNavigationEditor>
    </template>
  </WrappedForm>
</template>

<script>
import mixin from './mixin'
import CustomContentEditor from './customContentEditor.vue'
import VideoNavigationEditor from './videoNavigationEditor.vue'
import _ from 'lodash'
import { mapActions } from 'vuex'

const NON_IMAGE_SELECTOR_COMPANY_IDS = [8223]

const LANGUAGE_CODE = {
  EN: 'en',
  ZH_CN: 'zh-cn'
}

export default {
  mixins: [mixin],
  components: {
    CustomContentEditor,
    VideoNavigationEditor,
    TinymceEditor: () => import('./tinymceEditor.vue') // lazy load "tinymceEditor" component to reduce "chunk-vendors.js" file size
  },
  props: {
    rootCompany: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      customDescriptionEn: '',
      customDescriptionCn: '',
      hiddenTinymce: false
    }
  },
  computed: {
    formLines () {
      const vm = this
      const isTemplate = this.isTemplate
      const requiredMessage = this.$t('common.message.requiredFormField')
      const requiredRule = [{ required: true, message: requiredMessage, trigger: 'change' }]
      const lines = {
        showCompanyCustomDetailPage: {
          elType: 'el-radio-group',
          label: this.$t(`${this.i18nPrefix}.label.showCompanyCustomDetailPage`),
          option: {
            data: [
              { label: this.$t(`${this.i18nPrefix}.label.show`), value: true },
              { label: this.$t(`${this.i18nPrefix}.label.notShow`), value: false }
            ]
          }
        },
        pcCnDetailHeader: {
          label: this.$t(`${this.i18nPrefix}.label.pcCnDetailHeader`),
          contentSlot: 'customContentEditor'
        },
        mobileCnDetailHeader: {
          label: this.$t(`${this.i18nPrefix}.label.mobileCnDetailHeader`),
          contentSlot: 'customContentEditor'
        },
        cnDetail: {
          label: this.$t(`${this.i18nPrefix}.label.cnDetail`),
          contentSlot: 'jobDetailEditor',
          rules: requiredRule
        },
        pcCnDetailFooter: {
          label: this.$t(`${this.i18nPrefix}.label.pcCnDetailFooter`),
          contentSlot: 'customContentEditor'
        },
        mobileCnDetailFooter: {
          label: this.$t(`${this.i18nPrefix}.label.mobileCnDetailFooter`),
          contentSlot: 'customContentEditor',
          class: 'has-line'
        },
        pcEnDetailHeader: {
          label: this.$t(`${this.i18nPrefix}.label.pcEnDetailHeader`),
          contentSlot: 'customContentEditor'
        },
        mobileEnDetailHeader: {
          label: this.$t(`${this.i18nPrefix}.label.mobileEnDetailHeader`),
          contentSlot: 'customContentEditor'
        },
        enDetail: {
          label: this.$t(`${this.i18nPrefix}.label.enDetail`),
          contentSlot: 'jobDetailEditor'
        },
        pcEnDetailFooter: {
          label: this.$t(`${this.i18nPrefix}.label.pcEnDetailFooter`),
          contentSlot: 'customContentEditor'
        },
        mobileEnDetailFooter: {
          label: this.$t(`${this.i18nPrefix}.label.mobileEnDetailFooter`),
          contentSlot: 'customContentEditor'
        },
        cnVideoNavigation: {
          hidden: !this.hasVideoNavigationPermission,
          label: this.$t(`${this.i18nPrefix}.label.videoNavigationCn`),
          contentSlot: 'videoNavigation',
          props: {
            languageCode: LANGUAGE_CODE.ZH_CN,
            rootCompanyId: this.rootCompany?.id,
            data: _.orderBy(this.stepData.videoNavigation?.[0], ['order'])
          }
        },
        enVideoNavigation: {
          hidden: !this.hasVideoNavigationPermission,
          label: this.$t(`${this.i18nPrefix}.label.videoNavigationEn`),
          contentSlot: 'videoNavigation',
          props: {
            languageCode: LANGUAGE_CODE.EN,
            rootCompanyId: this.rootCompany?.id,
            data: _.orderBy(this.stepData.videoNavigation?.[1], ['order'])
          }
        }
      }
      const isOverseasCompany = this.isOverseasCompany
      const hasCreateJobPermission = this.hasCreateJobPermission
      const hasCreateJobFromTemplatePermission = this.hasCreateJobFromTemplatePermission
      const canCustomJobDetailInfo = this.canCustomJobDetailInfo
      const hiddenList = []
      const disabledList = []
      if (isOverseasCompany) {
        hiddenList.push(lines.pcCnDetailHeader, lines.mobileCnDetailHeader, lines.pcCnDetailFooter, lines.mobileCnDetailFooter)
        lines.enDetail.rules = requiredRule
      }
      if (isTemplate || !canCustomJobDetailInfo) {
        hiddenList.push(
          lines.pcCnDetailHeader,
          lines.mobileCnDetailHeader,
          lines.pcCnDetailFooter,
          lines.mobileCnDetailFooter,
          lines.pcEnDetailHeader,
          lines.mobileEnDetailHeader,
          lines.pcEnDetailFooter,
          lines.mobileEnDetailFooter
        )
      }
      if (isTemplate || !this.hasCustomJobDetailCompanyLevel) {
        hiddenList.push(lines.showCompanyCustomDetailPage)
      }
      if (isTemplate) {
        Object.defineProperty(isOverseasCompany ? lines.enDetail : lines.cnDetail, 'rules', {
          configurable: true,
          enumerable: true,
          get () {
            return vm.jobInfo.job_property === '1' ? null : requiredRule
          }
        })
      } else {
        if (hasCreateJobFromTemplatePermission && !hasCreateJobPermission) {
          disabledList.push(
            lines.cnDetail,
            lines.enDetail
          )
        }
      }
      _.forEach(hiddenList, line => {
        line.hidden = true
      })
      _.forEach(disabledList, line => {
        line.disabled = true
      })

      if (!this.isTemplate) {
        _.filter([
          lines.cnDetail,
          lines.enDetail
        ], line => {
          return !hiddenList.includes(line) && !disabledList.includes(line)
        }).forEach(line => {
          Object.defineProperty(line, 'disabled', {
            configurable: true,
            enumerable: true,
            get () {
              return vm.isNotEditableJobTemplate
            }
          })
        })
      }
      return lines
    },
    hiddenImage () {
      return NON_IMAGE_SELECTOR_COMPANY_IDS.includes(this.rootCompany?.id)
    },
    showTinymce () {
      return this.rootCompany
    },
    hasVideoNavigationPermission () {
      return this.hasAnyPermission('PREMIUM_VIDEO_NAVIGATION')
    }
  },
  created () {
  },
  methods: {
    ...mapActions('job', ['getJobCustomDescription']),
    async initJobCustomDescription () {
      if (!this.$route.path.startsWith('/v-admin/job/add')) {
        return
      }
      const companyId = this.companyDataParams?.company_id || this.jobInfo.company?.id
      const resp = await this.getJobCustomDescription({ company_id: companyId })
      if (resp) {
        this.customDescriptionEn = resp.en
        this.customDescriptionCn = resp.cn
      }
    },
    async customValidate () {
      let hasError = false
      let pcCnDetailHeaderRef = this.$refs.customContent_pcCnDetailHeader
      let mobileCnHeaderRef = this.$refs.customContent_mobileCnDetailHeader
      let cnVideoNavigation = this.$refs.navigation_cnVideoNavigation
      if (this.formLines.pcCnDetailHeader.hidden && this.formLines.mobileCnDetailHeader.hidden) {
        if (!cnVideoNavigation) {
          await this.$nextTick()
          cnVideoNavigation = this.$refs.navigation_cnVideoNavigation
        }
      } else {
        if (!pcCnDetailHeaderRef && !mobileCnHeaderRef) {
          await this.$nextTick()
          pcCnDetailHeaderRef = this.$refs.customContent_pcCnDetailHeader
          mobileCnHeaderRef = this.$refs.customContent_mobileCnDetailHeader
        }
      }
      if (!pcCnDetailHeaderRef && !mobileCnHeaderRef && !cnVideoNavigation) {
        return !hasError
      }
      const promiseList = _.map({
        pcCnDetailHeader: pcCnDetailHeaderRef,
        mobileCnDetailHeader: mobileCnHeaderRef,
        pcCnDetailFooter: this.$refs.customContent_pcCnDetailFooter,
        mobileCnDetailFooter: this.$refs.customContent_mobileCnDetailFooter,
        pcEnDetailHeader: this.$refs.customContent_pcEnDetailHeader,
        mobileEnDetailHeader: this.$refs.customContent_mobileEnDetailHeader,
        pcEnDetailFooter: this.$refs.customContent_pcEnDetailFooter,
        mobileEnDetailFooter: this.$refs.customContent_mobileEnDetailFooter,
        cnVideoNavigation: cnVideoNavigation,
        enVideoNavigation: this.$refs.navigation_enVideoNavigation
      }, (contentRef, lineName) => {
        if (hasError) {
          return null
        }
        return contentRef?.submit().then(data => {
          if (data) {
            this.stepData[lineName] = data
          } else {
            hasError = true
          }
        })
      })
      await Promise.all(promiseList)
      return !hasError
    },
    async setStepDataToJobInfo () {
      const stepData = this.stepData
      const jobInfo = this.jobInfo
      if (!this.isTemplate && this.canCustomJobDetailInfo) {
        this.$set(jobInfo, 'pc_customized_header_cn', JSON.stringify(stepData.pcCnDetailHeader || ''))
        this.$set(jobInfo, 'pc_customized_footer_cn', JSON.stringify(stepData.pcCnDetailFooter || ''))
        this.$set(jobInfo, 'pc_customized_header_en', JSON.stringify(stepData.pcEnDetailHeader || ''))
        this.$set(jobInfo, 'pc_customized_footer_en', JSON.stringify(stepData.pcEnDetailFooter || ''))
        this.$set(jobInfo, 'mobile_customized_header_cn', JSON.stringify(stepData.mobileCnDetailHeader || ''))
        this.$set(jobInfo, 'mobile_customized_footer_cn', JSON.stringify(stepData.mobileCnDetailFooter || ''))
        this.$set(jobInfo, 'mobile_customized_header_en', JSON.stringify(stepData.mobileEnDetailHeader || ''))
        this.$set(jobInfo, 'mobile_customized_footer_en', JSON.stringify(stepData.mobileEnDetailFooter || ''))
      }
      if (!this.isTemplate && this.hasCustomJobDetailCompanyLevel) {
        this.$set(jobInfo, 'enable_company_job_customization', stepData.showCompanyCustomDetailPage)
      }
      this.$set(jobInfo, 'cn_description', stepData.cnDetail)
      this.$set(jobInfo, 'description', stepData.enDetail)
      this.$set(jobInfo, 'video_navigation_list', [stepData.cnVideoNavigation?.video_list, stepData.enVideoNavigation?.video_list])
    },
    async getStepData () {
      await this.initJobCustomDescription()
      const jobInfo = this.jobInfo
      const stepData = {
        showCompanyCustomDetailPage: true,
        pcCnDetailHeader: [],
        mobileCnDetailHeader: [],
        cnDetail: jobInfo.cn_description || this.customDescriptionCn,
        pcCnDetailFooter: [],
        mobileCnDetailFooter: [],
        pcEnDetailHeader: [],
        mobileEnDetailHeader: [],
        enDetail: jobInfo.description || this.customDescriptionEn,
        pcEnDetailFooter: [],
        mobileEnDetailFooter: [],
        videoNavigation: jobInfo.video_navigation_list
      }
      if (!this.isTemplate && this.canCustomJobDetailInfo) {
        if (jobInfo.pc_customized_header_cn) {
          stepData.pcCnDetailHeader = JSON.parse(jobInfo.pc_customized_header_cn)
        }

        if (jobInfo.mobile_customized_header_cn) {
          stepData.mobileCnDetailHeader = JSON.parse(jobInfo.mobile_customized_header_cn)
        }

        if (jobInfo.pc_customized_footer_cn) {
          stepData.pcCnDetailFooter = JSON.parse(jobInfo.pc_customized_footer_cn)
        }

        if (jobInfo.mobile_customized_footer_cn) {
          stepData.mobileCnDetailFooter = JSON.parse(jobInfo.mobile_customized_footer_cn)
        }

        if (jobInfo.pc_customized_header_en) {
          stepData.pcEnDetailHeader = JSON.parse(jobInfo.pc_customized_header_en)
        }

        if (jobInfo.mobile_customized_header_en) {
          stepData.mobileEnDetailHeader = JSON.parse(jobInfo.mobile_customized_header_en)
        }

        if (jobInfo.pc_customized_footer_en) {
          stepData.pcEnDetailFooter = JSON.parse(jobInfo.pc_customized_footer_en)
        }

        if (jobInfo.mobile_customized_footer_en) {
          stepData.mobileEnDetailFooter = JSON.parse(jobInfo.mobile_customized_footer_en)
        }
      }
      if (!this.isTemplate && this.hasCustomJobDetailCompanyLevel) {
        stepData.showCompanyCustomDetailPage = typeof jobInfo.enable_company_job_customization === 'boolean' ? jobInfo.enable_company_job_customization : true
      }
      return stepData
    }
  },
  watch: {
    '$i18n.locale': {
      handler () {
        this.hiddenTinymce = true
        this.$nextTick(() => {
          this.hiddenTinymce = false
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.job-description-info {
  .el-form-item {
    &.is-error {
      .tinymce-editor-container {
        ::v-deep .tox-tinymce{
          border-color: #F56C6C;
        }
      }
    }
  }
  ::v-deep {
    .has-line {
      border-bottom: 1px solid #ebeef5;
      margin-bottom: 40px;
      padding-bottom: 40px;
    }
  }
}
</style>
